import { useState } from 'react'
import styled from 'styled-components'
import { Dropdown, MenuProps, Switch } from 'antd'
import {
  Add,
  FilterContainer,
  MultiSelect,
  // Select,
  RangePicker,
  GroupedResidentsMultiSelect,
} from '@lmc/ui-kit'
import { Button, Text } from '@lmc/ui-kit/src/general'
import dayjs from 'dayjs'
import { lowerFirst } from 'lodash'
import useUserSkills from 'screens/Rostering/hooks/useUserSkills'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { Home } from '@lmc/ts-common'
import { getNominative } from '@lmc/common/utils'
import { useShiftTypesList } from 'screens/Rostering/hooks/useShiftTypesList'
import { useVisitTypesList } from 'screens/Rostering/hooks/useVisitTypesList'
import { VISIT } from 'screens/Rostering/constants'
import useResidentsList from 'screens/Rostering/hooks/useResidentsList'

type FilterOptionKey =
  | 'Frequency'
  // | 'Flexibility'
  // | 'Team Members'
  | 'Skills Required'
  // | 'Runs'
  | 'Type'

export type RotaFiltersValue = {
  startDate?: Date
  endDate?: Date
  subjectIds?: string[]
  flexiType?: boolean
  // teamMembersNeeded?: number[]
  frequencies?: string[]
  skills?: string[]
  instanceTypeIds?: string[]
  showInactive?: boolean
}

const filterOptions: Record<FilterOptionKey, string[]> = {
  Frequency: ['One-off', 'Daily', 'Weekly', 'Monthly'],
  // Flexibility: ['Flexible', 'Fixed'],
  /* 'Team Members': [
    '2 or less required',
    '3 to 5 required',
    '6 or more required',
  ], */
  'Skills Required': [],
  // Runs: ['Run 1', 'Run 2', 'Run 3'],
  Type: [],
}
const initialAvailableFilters: FilterOptionKey[] = [
  'Frequency',
  // 'Flexibility',
  // 'Team Members',
  'Skills Required',
  // 'Runs',
  'Type',
]

type RotaFiltersProps = {
  value?: RotaFiltersValue
  onChange: (value: RotaFiltersValue) => void
  hasResidentsFilter?: boolean
  filtersType?: 'visit' | 'shift'
}

const RotaFilters = ({
  value,
  onChange,
  hasResidentsFilter = true,
  filtersType,
}: RotaFiltersProps) => {
  const [availableFilters, setAvailableFilters] = useState<FilterOptionKey[]>(
    initialAvailableFilters
  )
  const [addedFilters, setAddedFilters] = useState<FilterOptionKey[]>([])

  const { allSkills } = useUserSkills()

  const { visitTypes } = useVisitTypesList()
  const { shiftTypes } = useShiftTypesList()

  const { t } = useTranslation()

  const { residents } = useResidentsList({})

  const preferredPeopleNominative = useSelector(
    (state: { global: { home: Home } }) =>
      state.global.home.preferredPeopleNominative
  )
  const residentNominative = getNominative(
    preferredPeopleNominative,
    'Resident'
  )

  const handleAddFilter: MenuProps['onClick'] = ({ key }) => {
    const filterKey = key as FilterOptionKey
    setAddedFilters((prev) => [...prev, filterKey])
    setAvailableFilters((prev) => prev.filter((filter) => filter !== filterKey))
  }

  const dropdownItems = availableFilters.map((filter) =>
    filter === 'Type'
      ? {
          key: filter,
          label:
            filtersType === VISIT ? t('TYPE_OF_VISIT') : t('TYPE_OF_SHIFT'),
        }
      : {
          key: filter,
          label: filter,
        }
  )

  const transformOptions = (options: string[]) => {
    return options.map((option) => ({ value: option, label: option }))
  }

  const requiredSkills = allSkills?.map((skill) => ({
    label: skill?.name,
    value: skill?.id,
  }))

  const instanceTypes = (filtersType === VISIT ? visitTypes : shiftTypes)?.map(
    (type) => ({
      label: type.name,
      value: type.id,
    })
  )

  const handleInputChange = (newValue: Partial<RotaFiltersValue>) => {
    const updatedValue: RotaFiltersValue = {
      ...value,
      ...newValue,
    }
    onChange(updatedValue)
  }

  // please don't use render functions like this from now
  const renderFilterComponent = (filter: FilterOptionKey) => {
    switch (filter) {
      // case 'Flexibility':
      //   return (
      //     <StyledSelect
      //       data-testid="flexibility-select"
      //       value={
      //         value?.flexiType !== undefined
      //           ? value.flexiType
      //             ? 'Flexible'
      //             : 'Fixed'
      //           : null
      //       }
      //       key={filter}
      //       placeholder={<Text level={1}>{t(filter)}</Text>}
      //       allowClear
      //       onChange={(flexibility) =>
      //         handleInputChange({
      //           flexiType: !flexibility
      //             ? undefined
      //             : (flexibility as string) === 'Flexible',
      //         })
      //       }
      //     >
      //       {filterOptions[filter]?.map((option) => (
      //         <Select.Option key={String(option)} value={option} label={option}>
      //           <OptionContainer>
      //             <StyledText>{option}</StyledText>
      //           </OptionContainer>
      //         </Select.Option>
      //       ))}
      //     </StyledSelect>
      //   )
      /* case 'Team Members':
        return (
          <StyledWideMultiSelect
            key={filter}
            title={t(filter)}
            capitalizedLabel={false}
            options={transformOptions(filterOptions[filter])}
          />
        ) */
      case 'Frequency':
        return (
          <StyledWideMultiSelect
            key={filter}
            title={t(filter)}
            capitalizedLabel={false}
            hasClear
            hasSelectAll
            options={transformOptions(filterOptions[filter])}
            onChange={(frequency) =>
              handleInputChange({
                frequencies: (frequency as string[]).map((freq) =>
                  lowerFirst(freq.replace(/-/g, ' '))
                ),
              })
            }
          />
        )
      case 'Skills Required':
        return (
          <StyledWideMultiSelect
            values={value?.skills}
            title={t(filter)}
            options={requiredSkills}
            hasClear
            hasSearch
            hasSelectAll
            onChange={(reqSkills) =>
              handleInputChange({
                skills: reqSkills as string[],
              })
            }
          />
        )
      case 'Type':
        return (
          <StyledWideMultiSelect
            values={value?.instanceTypeIds}
            title={
              filtersType === VISIT ? t('TYPE_OF_VISIT') : t('TYPE_OF_SHIFT')
            }
            options={instanceTypes}
            hasClear
            hasSearch
            hasSelectAll
            onChange={(types) =>
              handleInputChange({
                instanceTypeIds: types as string[],
              })
            }
          />
        )
      default:
        return (
          <StyledMultiSelect
            key={filter}
            title={t(filter)}
            options={transformOptions(filterOptions[filter])}
          />
        )
    }
  }

  return (
    <FilterContainer title="Filters">
      <Filters>
        <LeftContainer>
          <RangePicker
            value={[
              value?.startDate ? dayjs(value?.startDate) : null,
              value?.endDate ? dayjs(value?.endDate) : null,
            ]}
            onChange={(rangePicker) =>
              handleInputChange({
                startDate: rangePicker
                  ? (rangePicker[0] as dayjs.Dayjs).toDate()
                  : undefined,
                endDate: rangePicker
                  ? (rangePicker[1] as dayjs.Dayjs).toDate()
                  : undefined,
              })
            }
            placeholder={[t('FROM'), t('TO')]}
          />
          {hasResidentsFilter && (
            <GroupedResidentsMultiSelect
              residents={residents}
              title={residentNominative}
              values={value?.subjectIds}
              handleInputChange={(selectedOptions) =>
                handleInputChange({
                  subjectIds: selectedOptions as string[],
                })
              }
            />
          )}
          {/* <StyledMultiSelect title="Rota tags" /> */}
          {addedFilters.map((filter) => renderFilterComponent(filter))}
          {addedFilters.length !== initialAvailableFilters.length && (
            <ButtonContainer>
              <Dropdown
                menu={{ items: dropdownItems, onClick: handleAddFilter }}
                trigger={['click']}
              >
                <Button variant="text">
                  <Add /> Add Filter
                </Button>
              </Dropdown>
            </ButtonContainer>
          )}
        </LeftContainer>
        <RightContainer>
          <SpotlightText weight="medium">{t('SHOW_INACTIVE')}</SpotlightText>
          <Switch
            value={value?.showInactive}
            onChange={(inactive) =>
              handleInputChange({
                showInactive: inactive,
              })
            }
          />
        </RightContainer>
      </Filters>
    </FilterContainer>
  )
}

const LeftContainer = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`

const RightContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const StyledMultiSelect = styled(MultiSelect)`
  min-width: 160px;
`

const StyledWideMultiSelect = styled(MultiSelect)`
  min-width: 200px;
` as unknown as typeof MultiSelect

// const StyledSelect = styled(Select)`
//   min-width: 160px;
// `

const ButtonContainer = styled.div`
  display: flex;
  align-items: center;
`

const Filters = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
`

// const OptionContainer = styled.div`
//   display: flex;
//   align-items: center;
//   padding: 0 8px;
// `

// const StyledText = styled(Text)`
//   color: ${(props) => props.theme.color.neutral.color3};
// `

const SpotlightText = styled(Text)`
  color: ${(props) => props.theme.color.neutral.color5};
`

export default RotaFilters
